import { FC, useEffect, useMemo, useState } from 'react';
import { Button, Card, RadixTable, TColumn } from '@features/ui';
import { formatCurrency, formatDate } from '@lib/utils.ts';
import { TReportProfitableTradersTable } from '@src/types';
import Pagination from '@features/ui/components/pagination';

interface ReportProfitableTradersTableProps {
  data: TReportProfitableTradersTable[];
}

export const ReportProfitableTradersTable: FC<
  ReportProfitableTradersTableProps
> = ({ data }) => {
  const pageSize = 50;
  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = Math.max(1, Math.ceil(data.length / pageSize));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;

    return data.slice(start, start + pageSize);
  }, [currentPage, data]);

  const renderDate = (value?: string) => {
    if (!value) {
      return '-';
    }

    if (value.includes('T')) {
      return formatDate(value);
    }

    return value;
  };

  const columns = useMemo(
    (): TColumn<TReportProfitableTradersTable>[] => [
      {
        title: 'Account No',
        render: (item) => (
          <div className={'flex flex-col gap-1'}>
            <Button
              className={'text-[#3490FF] underline w-fit pl-0'}
              variant={'link'}
            >
              {item?.account_id}
            </Button>
            <p>{item?.account_name}</p>
          </div>
        ),
      },
      {
        title: 'User',
        render: (item) => (
          <div className={'flex flex-col gap-1'}>
            <Button
              className={'text-[#3490FF] underline w-fit pl-0'}
              variant={'link'}
            >
              {item?.user_id}
            </Button>
            <p>{item?.user}</p>
          </div>
        ),
      },
      {
        title: `Package`,
        render: (item) => <p>{item?.package ?? '-'}</p>,
      },
      {
        title: `Trading Days`,
        className: 'xl:pl-10 pl-2',
        render: (item) => (
          <div
            className={
              'w-8 h-6.5 rounded-[3px] bg-lime flex items-center justify-center'
            }
          >
            <p>{item?.trading_days ?? 0}</p>
          </div>
        ),
      },
      {
        title: `Win %`,
        render: (item) => (
          <div
            className={
              'h-6.5 rounded-[3px] bg-lime flex items-center justify-center w-[70px]'
            }
          >
            {item?.win_percent ?? 0}%
          </div>
        ),
      },
      {
        title: `Balance`,
        render: (item) => (
          <div className={'flex flex-col gap-1'}>
            <p>$ {formatCurrency(parseFloat(item?.balance ?? '0'))}</p>
            <p>{renderDate(item?.updated)}</p>
          </div>
        ),
      },
      {
        title: `ALTV`,
        render: (item) => (
          <p>$ {formatCurrency(parseFloat(item?.altv ?? '0'))}</p>
        ),
      },
      {
        title: `Acc Payouts`,
        render: (item) => (
          <div className={'flex flex-col gap-1'}>
            <p>
              $ {formatCurrency(parseFloat(item?.total_payout_amount ?? '0'))}
            </p>
            <p>{renderDate(item?.last_payout)}</p>
            <div
              className={
                'w-fit h-6.5 flex items-center justify-center bg-lime px-1 min-w-[120px] rounded-[3px]'
              }
            >
              <p>TOTAL PAYOUTS: {item?.total_payouts}</p>
            </div>
          </div>
        ),
      },
      {
        title: `User Payouts`,
        render: (item) => (
          <p>
            ${' '}
            {formatCurrency(parseFloat(item?.total_payout_amount_user ?? '0'))}
          </p>
        ),
      },
    ],
    []
  );

  return (
    <Card className={'mt-4.5'}>
      <RadixTable columns={columns} data={paginatedData} />
      {data.length > pageSize && (
        <Pagination
          className={'justify-end mt-4'}
          current={currentPage}
          total={totalPages}
          onChange={setCurrentPage}
        />
      )}
    </Card>
  );
};
